import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TokenService } from '../shared/services/token.service';
import { JwtHelperService } from '@auth0/angular-jwt';
import { AppConfigService } from '../shared/services/app-config.service';

@Component({
  selector: 'app-auth',
  templateUrl: './auth.component.html',
  styleUrls: ['./auth.component.scss'],
})
export class AuthComponent implements OnInit, OnDestroy {
  private unsubscribe = new Subject<void>();

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private _tokenService: TokenService,
    private jwtService: JwtHelperService,
    private appConfig: AppConfigService
  ) { }

  ngOnInit(): void {
    if (this.appConfig.bypassAuth) {
      this.router.navigateByUrl('/dashboard');
      return;
    }

    this.activatedRoute.queryParams
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(async params => {
        const urlToken = params['token'];
        const redirect = params['redirectUrl'] || '/dashboard';

        const localToken = this._tokenService.token;
        const ssoToken = this._tokenService.ssoAccessToken;

        // Normal app login
        if (urlToken) {
          try {
            localStorage.setItem('redirectUrl', redirect);
            this._tokenService.saveToken(urlToken);

            const payload: any = this.jwtService.decodeToken(urlToken);

            if (payload) {
              this._tokenService.saveUser(payload);

              await this.appConfig.loadCompanyConfiguration();

              this.router.navigateByUrl('/dashboard');
            } else {
              this.router.navigateByUrl('/unauthorized');
            }
          } catch (e) {
            console.error('Login error (url token):', e);
            this.router.navigateByUrl('/unauthorized');
          }
          return;
        }

        // SSO login
        if (!localToken && ssoToken) {
          try {
            if (!localStorage.getItem('redirectUrl')) {
              localStorage.setItem('redirectUrl', redirect);
            }

            const decoded: any = this.jwtService.decodeToken(ssoToken);

            if (!decoded) {
              this.router.navigateByUrl('/unauthorized');
              return;
            }

            const id =
              decoded.nameid ??
              decoded.UserID ??
              decoded.sid ??
              decoded.sub ??
              decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/sid'] ??
              decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'] ??
              null;

            this._tokenService.saveUser({
              nameid: id,
              UserID: id,
              fullName: decoded.unique_name ?? decoded.name ?? '',
              userPortals: decoded.userPortals ?? [],
            });

            await this.appConfig.loadCompanyConfiguration();

            const redirectUrl =
              localStorage.getItem('redirectUrl') || '/dashboard';

            localStorage.removeItem('redirectUrl');
            this.router.navigateByUrl(redirectUrl);

          } catch (e) {
            console.error('Login error (SSO cookie):', e);
            this.router.navigateByUrl('/unauthorized');
          }
          return;
        }

        // Already authenticated
        if (localToken) {
          await this.appConfig.loadCompanyConfiguration();
          return;
        }

        // No authentication
        if (!urlToken && !ssoToken) {
          window.location.href = this.appConfig.portalUrl;
        }
      });
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }
}
